import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { trackViewDocs } from '../utils/analytics'

export default function DeveloperDocsPage() {
  // Track developer docs view
  useEffect(() => {
    trackViewDocs('developer_docs')
  }, [])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Developer Documentation
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Everything you need to integrate ContentIndexer into your application
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="/openapi.yaml"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-primary-600 text-white rounded-xl p-6 hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center mb-3">
              <i className="fas fa-book text-3xl mr-4"></i>
              <h2 className="text-2xl font-bold">API Reference</h2>
            </div>
            <p className="text-primary-100">
              Interactive Swagger UI with all endpoints, request/response schemas, and try-it-out functionality
            </p>
          </a>

          <Link
            to="/dashboard"
            className="block bg-green-600 text-white rounded-xl p-6 hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center mb-3">
              <i className="fas fa-key text-3xl mr-4"></i>
              <h2 className="text-2xl font-bold">Get API Keys</h2>
            </div>
            <p className="text-green-100">
              Create and manage API keys from your dashboard to start making requests
            </p>
          </Link>
        </div>

        {/* Getting Started */}
        <section className="mb-12 bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            <i className="fas fa-rocket text-primary-600 mr-3"></i>
            Quick Start
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                1. Get Your API Key
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-3">
                Sign up and navigate to your dashboard to create an API key. Keep it secure!
              </p>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 font-mono text-sm">
                <span className="text-gray-500 dark:text-gray-400">Your API key format:</span>
                <br />
                <span className="text-primary-600 dark:text-primary-400">ci_live_abc123...</span>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                2. Make Your First Request
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-3">
                Test the API with a simple cURL command:
              </p>
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono">
{`curl -X GET "https://api.contentindexer.com/v1/user/me" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                </pre>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                3. Create a Processing Job
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-3">
                Submit content for processing with your preferred extractors:
              </p>
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono">
{`curl -X POST "https://api.contentindexer.com/v1/jobs" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content_url": "https://example.com/video.mp4",
    "workflow": "ffprobe,thumbnail"
  }'`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Authentication */}
        <section className="mb-12 bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            <i className="fas fa-shield-alt text-primary-600 mr-3"></i>
            Authentication
          </h2>

          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              All API requests must include your API key in the Authorization header:
            </p>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="font-mono text-sm">
                <span className="text-gray-600 dark:text-gray-400">Authorization:</span>{' '}
                <span className="text-primary-600 dark:text-primary-400">Bearer YOUR_API_KEY</span>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mr-3 mt-1"></i>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <p className="font-semibold mb-1">Security Best Practices:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Never commit API keys to version control</li>
                    <li>Use environment variables to store keys</li>
                    <li>Rotate keys regularly</li>
                    <li>Use separate keys for development and production</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Code Examples */}
        <section className="mb-12 bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            <i className="fas fa-code text-primary-600 mr-3"></i>
            Code Examples
          </h2>

          <div className="space-y-6">
            {/* JavaScript Example */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <i className="fab fa-js-square text-yellow-500 mr-2"></i>
                JavaScript / Node.js
              </h3>
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono">
{`const API_KEY = process.env.CONTENTINDEXER_API_KEY;
const API_BASE = 'https://api.contentindexer.com';

async function createJob(contentUrl, workflow) {
  const response = await fetch(\`\${API_BASE}/v1/jobs\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${API_KEY}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      content_url: contentUrl,
      workflow: workflow
    })
  });

  const job = await response.json();
  console.log('Job created:', job.id);
  return job;
}

// Example usage
createJob('https://example.com/video.mp4', 'ffprobe,thumbnail');`}
                </pre>
              </div>
            </div>

            {/* Python Example */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <i className="fab fa-python text-blue-500 mr-2"></i>
                Python
              </h3>
              <div className="bg-gray-900 dark:bg-black rounded-lg p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono">
{`import os
import requests

API_KEY = os.environ['CONTENTINDEXER_API_KEY']
API_BASE = 'https://api.contentindexer.com'

def create_job(content_url, workflow):
    response = requests.post(
        f'{API_BASE}/v1/jobs',
        headers={
            'Authorization': f'Bearer {API_KEY}',
            'Content-Type': 'application/json'
        },
        json={
            'content_url': content_url,
            'workflow': workflow
        }
    )

    job = response.json()
    print(f"Job created: {job['id']}")
    return job

# Example usage
create_job('https://example.com/video.mp4', 'ffprobe,thumbnail')`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Environments */}
        <section className="mb-12 bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            <i className="fas fa-server text-primary-600 mr-3"></i>
            API Environments
          </h2>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900 dark:text-white">Production</h3>
                <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs px-2 py-1 rounded font-semibold">
                  LIVE
                </span>
              </div>
              <code className="text-sm text-primary-600 dark:text-primary-400">
                https://api.contentindexer.com
              </code>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Use production API keys. Your account will be charged for usage.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900 dark:text-white">Development</h3>
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs px-2 py-1 rounded font-semibold">
                  DEV
                </span>
              </div>
              <code className="text-sm text-primary-600 dark:text-primary-400">
                https://api.dev.contentindexer.com
              </code>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                For testing and development. Use dev API keys.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900 dark:text-white">Local</h3>
                <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs px-2 py-1 rounded font-semibold">
                  LOCAL
                </span>
              </div>
              <code className="text-sm text-primary-600 dark:text-primary-400">
                http://localhost:8787
              </code>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Run the API locally with Wrangler for offline development.
              </p>
            </div>
          </div>
        </section>

        {/* Support */}
        <section className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-8 border border-primary-200 dark:border-primary-800">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            <i className="fas fa-life-ring text-primary-600 mr-3"></i>
            Need Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We're here to help you succeed with ContentIndexer.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="/openapi.yaml"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white dark:bg-gray-800 rounded-lg p-4 hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <i className="fas fa-book text-primary-600 mr-2"></i>
              <span className="font-semibold text-gray-900 dark:text-white">Browse API Docs</span>
            </a>
            <a
              href="https://github.com/contentindexer/examples"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white dark:bg-gray-800 rounded-lg p-4 hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <i className="fab fa-github text-primary-600 mr-2"></i>
              <span className="font-semibold text-gray-900 dark:text-white">Code Examples</span>
            </a>
          </div>
        </section>
      </div>
    </div>
  )
}
